import {Injectable, OnDestroy} from "@angular/core";
import {SystemConfigurationValues} from "../../../common/configuration/services/system.configuration.utils";
import {AssigningAuthority, LookupResponse, LookupsService} from "../../lookups";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {Patient} from "../../clinical/patient/models/patient";
import {PatientId} from "../../clinical/patient/models/patient.id";
import {Observable, of} from "rxjs";
import {map} from "rxjs/operators";

@UntilDestroy()
@Injectable({ providedIn: 'root' })
  export class PatientIdIssuerService implements OnDestroy {
	private issuers: AssigningAuthority.AssigningAuthority[];
	private defaultIdIssuer: string;

	constructor(private systemConfigurationValues: SystemConfigurationValues,
				private lookupsService: LookupsService) {
		this.setIssuers();
	}

	ngOnDestroy() { }

	/**
	 * Gets patient id issuer determined by configuration that is used by default for creating new patient.
	 * @returns {number} assigning authority key of default patient id issuer.
	 */
	public getDefaultPatientIdIssuer(): number {
		return this.systemConfigurationValues.getNumber("NewPatientIDAssigningAuthorityKey");
	}

	/**
	 * Checks if patient id issuer equals to patient id issuer used by default for creating new patient.
	 * @param issuer - key or display name.
	 * @returns true if patient id is default; false otherwise.
	 */
	public isDefaultPatientIdIssuer(issuer: number|string): boolean {
		if (typeof issuer === "string") {
			return this.defaultIdIssuer ? issuer.toUpperCase() === this.defaultIdIssuer.toUpperCase() : false;
		} else {
			return issuer === this.getDefaultPatientIdIssuer();
		}
	}

	/**
	 * Checks if issuers dropdown will be available for create new patient form.
	 * @returns {boolean}
	 */
	public isMultipleAssigningAuthoritiesAllowed(): boolean {
		return this.systemConfigurationValues.getBoolean("allowMultipleAssigningAuthorities");
	}

	/**
	 * Returns issuer name by issuer key
	 * @param key
	 */
	public getIssuerByIssuerKey(key: number): string {
		if (this.issuers) {
			let issuer = this.issuers.find(i => i.assigningAuthorityKey === key);
			return issuer ? issuer.displayName : null;
		} else {
			this.lookupsService.getLookup(this.lookupsService.createRequestGetAllRecords(AssigningAuthority.lookupName))
				.pipe(untilDestroyed(this))
				.subscribe((res: LookupResponse<AssigningAuthority.AssigningAuthority[]>) => {
					this.issuers = res.lookupTable.data;
					let issuer = this.issuers.find(i => i.assigningAuthorityKey === key);
					return issuer ? issuer.displayName : null;
				});
		}
	}

	/**
	 * Returns the observable of the primary patient id issuer key
	 * @param value
	 */
	public getPrimaryIdIssuerKeyObservable(value: Patient|PatientId|PatientId[]): Observable<number> {
		let rows = [];
		if (value instanceof Patient) {
			rows = value.patientIdList;
		} else if (Array.isArray(value)) {
			if (!value.length) {
				return of(null);
			} else {
				rows = value;
			}
		} else if (value) {
			rows = [value];
		} else {
			return of(null);
		}

		let primaryPatientId = rows.find(r => r.primary.toUpperCase() === 'Y');
		if (primaryPatientId) {
			if (primaryPatientId.issuerOfPidKey) {
				return of(primaryPatientId.issuerOfPidKey);
			} else if (primaryPatientId.issuerOfPid) {
				if (this.issuers) {
					let currIssuer = this.issuers.find(i => i.displayName.toLowerCase() === primaryPatientId.issuerOfPid.toLowerCase());
					if (currIssuer) {
						return of(currIssuer.assigningAuthorityKey);
					}
				} else {
					return this.lookupsService.getLookup(this.lookupsService.createRequestGetAllRecords(AssigningAuthority.lookupName))
						.pipe(map((res: LookupResponse<AssigningAuthority.AssigningAuthority[]>) => {
							this.issuers = res.lookupTable.data;
							let currIssuer = res.lookupTable.data.find(x => x.displayName.toLowerCase() === primaryPatientId.issuerOfPid.toLowerCase());
							return currIssuer ? currIssuer.assigningAuthorityKey : null;
						}));
				}
			}
		}
		return of(null);
	}

	/**
	 * Gets default patient id issuer display name and saves it in local variable.
	 */
	private setIssuers() {
		this.lookupsService.getLookup(this.lookupsService.createRequestGetAllRecords(AssigningAuthority.lookupName))
			.pipe(untilDestroyed(this))
			.subscribe((res: LookupResponse<AssigningAuthority.AssigningAuthority[]>) => {
				this.issuers = res.lookupTable.data;
				this.defaultIdIssuer = res.lookupTable.data.find(x => x.assigningAuthorityKey === this.getDefaultPatientIdIssuer()).displayName;
			});
	}
}
